/**
 * Phần tính thuần của thẻ «Tình hình kinh doanh»: cây nút máy chủ trả (đã cắt phạm
 * vi), số gộp khi người xem có nhiều nút gốc, chênh so tháng trước. Không gọi
 * máy chủ, không vẽ.
 */
import { LEAD_SOURCES } from "@/features/orders/pipeline/types";
import { labelOrderLeadSource } from "@/lib/orderUtils";
import type { SalesTeamOverview, SalesTeamOverviewMetrics, SalesTeamOverviewNode } from "./api";

export const NODE_KIND_LABEL: Record<string, string> = {
  company: "Toàn phòng",
  region: "Miền",
  team: "Đội",
  squad: "Tiểu đội",
  person: "Người",
};

/** Nút gốc: không có cha, hoặc cha nằm ngoài phạm vi người xem. */
export function rootNodes(o: SalesTeamOverview): SalesTeamOverviewNode[] {
  const ids = new Set(o.nodes.map((n) => n.id));
  return o.nodes.filter((n) => !n.parentId || !ids.has(n.parentId));
}

export function childNodes(o: SalesTeamOverview, id: string): SalesTeamOverviewNode[] {
  return o.nodes.filter((n) => n.parentId === id);
}

/** Đường từ gốc tới nút `id` (gồm cả nút đó). */
export function pathTo(o: SalesTeamOverview, id: string): SalesTeamOverviewNode[] {
  const byId = new Map(o.nodes.map((n) => [n.id, n]));
  const out: SalesTeamOverviewNode[] = [];
  const seen = new Set<string>();
  let cur = byId.get(id);
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    out.unshift(cur);
    cur = cur.parentId ? byId.get(cur.parentId) : undefined;
  }
  return out;
}

function addMetrics(a: SalesTeamOverviewMetrics, b: SalesTeamOverviewMetrics): SalesTeamOverviewMetrics {
  return {
    realRevenue: a.realRevenue + b.realRevenue,
    targetRevenue: a.targetRevenue + b.targetRevenue,
    target: a.target + b.target,
    hasTarget: a.hasTarget || b.hasTarget,
    collected: a.collected + b.collected,
    expected: a.expected + b.expected,
    orders: a.orders + b.orders,
    upsell: a.upsell + b.upsell,
    overdueLeads: a.overdueLeads == null && b.overdueLeads == null ? null : (a.overdueLeads ?? 0) + (b.overdueLeads ?? 0),
  };
}

function addSeries(a: number[], b: number[]): number[] {
  const n = Math.max(a.length, b.length);
  return Array.from({ length: n }, (_, i) => (a[i] ?? 0) + (b[i] ?? 0));
}

/**
 * Gộp nhiều nút gốc rời nhau (vd CSKH ở hai tiểu đội) thành một nút xem chung. Một
 * nút gốc thì trả nguyên nút đó.
 */
export function combineNodes(nodes: SalesTeamOverviewNode[], name: string): SalesTeamOverviewNode | null {
  if (nodes.length === 0) return null;
  if (nodes.length === 1) return nodes[0] ?? null;
  const [first, ...rest] = nodes as [SalesTeamOverviewNode, ...SalesTeamOverviewNode[]];
  return rest.reduce<SalesTeamOverviewNode>(
    (acc, n) => ({
      ...acc,
      metrics: addMetrics(acc.metrics, n.metrics),
      prev: acc.prev && n.prev ? addMetrics(acc.prev, n.prev) : null,
      prevFull: acc.prevFull && n.prevFull ? addMetrics(acc.prevFull, n.prevFull) : null,
      daily: addSeries(acc.daily, n.daily),
      trend: acc.trend.map((t) => {
        const o = n.trend.find((x) => x.month === t.month);
        return o ? { ...t, realRevenue: t.realRevenue + o.realRevenue, expected: t.expected + o.expected } : t;
      }),
      sources: mergeSources(acc.sources, n.sources),
    }),
    { ...first, id: "__chung", kind: "company", parentId: "", name },
  );
}

function mergeSources(a: SalesTeamOverviewNode["sources"], b: SalesTeamOverviewNode["sources"]) {
  const m = new Map(a.map((s) => [s.source, { ...s }]));
  for (const s of b) {
    const cur = m.get(s.source);
    if (cur) {
      cur.orders += s.orders;
      cur.expected += s.expected;
    } else m.set(s.source, { ...s });
  }
  return [...m.values()].sort((x, y) => y.expected - x.expected || x.source.localeCompare(y.source));
}

/** Cách so với tháng trước của mọi ô — theo `prevUntil` máy chủ trả (CEO 1a · 2a, 04/11/2026). */
export type PrevCompare = {
  /** Cùng kỳ chưa trọn tháng (đang xem tháng chạy dở). */
  partial: boolean;
  /** Sau "▲ 12% …": "so cùng kỳ 1–4/10" hoặc "so tháng trước". */
  short: string;
  /** Tiêu đề cột bảng từng người. */
  header: string;
  /** "tháng 10" — cho dòng "Cả tháng 10: …". */
  monthName: string;
};

export function prevCompare(prevUntil: string | null | undefined): PrevCompare {
  const [y, m, d] = (prevUntil ?? "").split("-").map(Number);
  if (!y || !m || !d) return { partial: false, short: "so tháng trước", header: "So tháng trước", monthName: "tháng trước" };
  const monthName = `tháng ${m}`;
  if (d >= daysInMonth(`${y}-${String(m).padStart(2, "0")}`)) {
    return { partial: false, short: "so tháng trước", header: "So tháng trước", monthName };
  }
  const range = d === 1 ? `1/${m}` : `1–${d}/${m}`;
  return { partial: true, short: `so cùng kỳ ${range}`, header: "So cùng kỳ", monthName };
}

/** % chênh so tháng trước (cùng kỳ hoặc trọn tháng); null khi không có tháng trước hoặc tháng trước bằng 0. */
export function deltaPercent(cur: number, prev: number | null | undefined): number | null {
  if (prev == null || prev === 0) return null;
  return Math.round(((cur - prev) / Math.abs(prev)) * 100);
}

/** Phần trăm đạt mục tiêu (số nguyên); null khi chưa đặt mục tiêu. */
export function targetPercent(m: SalesTeamOverviewMetrics): number | null {
  if (!m.hasTarget || m.target <= 0) return null;
  return Math.round((m.targetRevenue / m.target) * 100);
}

export function averageOrder(m: SalesTeamOverviewMetrics): number | null {
  return m.orders > 0 ? Math.round(m.expected / m.orders) : null;
}

const SOURCE_LABEL: Record<string, string> = {
  ...Object.fromEntries(LEAD_SOURCES.map((s) => [s.value, s.label])),
  returning: "Khách quay lại",
  pancake: "Pancake",
  unknown: "Chưa rõ",
};

/** Nhãn nguồn khách trên đơn (`orders.lead_source`). */
export function sourceLabel(raw: string): string {
  return SOURCE_LABEL[raw.toLowerCase()] ?? labelOrderLeadSource(raw);
}

/** Số ngày của tháng "YYYY-MM". */
export function daysInMonth(month: string): number {
  const [y, m] = month.split("-").map(Number);
  if (!y || !m) return 30;
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/**
 * Dữ liệu biểu đồ cộng dồn: mỗi ngày đã qua một điểm doanh thu, đường mục tiêu chia đều
 * theo ngày cho TRỌN tháng (để thấy đang nhanh hay chậm so với nhịp cần đi).
 */
export function cumulativeSeries(node: SalesTeamOverviewNode, month: string) {
  const days = daysInMonth(month);
  const target = node.metrics.hasTarget && node.metrics.target > 0 ? node.metrics.target : null;
  return Array.from({ length: days }, (_, i) => ({
    day: String(i + 1).padStart(2, "0"),
    actual: i < node.daily.length ? (node.daily[i] ?? null) : null,
    target: target == null ? null : Math.round((target * (i + 1)) / days),
  }));
}
